import { useState } from "react";
import { useI18n } from "../../i18n/I18nProvider";
import { safeFileName } from "../../lib/format";
import { exportConfiguration, type ExportProgress } from "./export-bundle";
import { openSaveSink } from "./save-sink";

/**
 * Runs a .goalhorns export for any configuration in the workspace. The save picker opens
 * synchronously inside `start`, so call it straight from a click handler (user gesture).
 */
export function useConfigExport(workspaceId: string) {
  const { t } = useI18n();
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);

  const start = async (configurationId: string, configurationName: string): Promise<void> => {
    const fileName = `${safeFileName(configurationName)}.goalhorns`;
    setError(null);
    setMessage(null);
    try {
      const sink = await openSaveSink(fileName);
      if (!sink) {
        setMessage(t("export.cancelled"));
        return;
      }
      setProgress({ phase: "preparing", done: 0, total: 0 });
      await exportConfiguration({ workspaceId, configurationId, sink, onProgress: setProgress });
      setMessage(t("export.done", { name: fileName }));
    } catch (failure: unknown) {
      setError(failure);
    } finally {
      setProgress(null);
    }
  };

  return { start, progress, error, message, busy: progress !== null };
}

export type ConfigExport = ReturnType<typeof useConfigExport>;
